/**
 * ICE server configuration shared by the 1:1 screen share and meetings.
 * Fetched from `GET /realtime/ice-servers/` (STUN always; TURN with
 * short-lived HMAC credentials when coturn is provisioned) and cached
 * in-memory until ~80% of the credential TTL. Falls back to public STUN
 * so dev keeps working with zero infrastructure.
 */

import apiClient from "@/api/client";

export const FALLBACK_ICE_SERVERS: RTCIceServer[] = [
  { urls: ["stun:stun.l.google.com:19302"] },
];

interface IceServersResponse {
  ice_servers: RTCIceServer[];
  ttl_seconds: number;
}

let cached: { servers: RTCIceServer[]; expiresAt: number } | null = null;

export async function getIceServers(): Promise<RTCIceServer[]> {
  if (cached && Date.now() < cached.expiresAt) return cached.servers;
  try {
    const r = await apiClient.get<{ data: IceServersResponse }>(
      "/realtime/ice-servers/",
    );
    const { ice_servers, ttl_seconds } = r.data.data;
    if (Array.isArray(ice_servers) && ice_servers.length > 0) {
      setIceServers(ice_servers, ttl_seconds);
      return ice_servers;
    }
  } catch {
    // Endpoint unavailable — stale cache beats no TURN at all.
  }
  return cached?.servers ?? FALLBACK_ICE_SERVERS;
}

/** Seed the cache directly — meeting guests get servers in their join response. */
export function setIceServers(servers: RTCIceServer[], ttlSeconds: number): void {
  cached = {
    servers,
    expiresAt: Date.now() + Math.max(60, ttlSeconds * 0.8) * 1000,
  };
}
